import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import LogoGalarya from "@/components/LogoGalarya";
import { homeDe } from "@/lib/home";
import { destinoSeguro } from "@/lib/retorno";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Enlace de aprobación abierto sin sesión: el guard conservó el destino interno y aquí se
  // regresa a él en vez de llevar al tablero de inicio.
  const retorno = destinoSeguro(location.state?.from);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      navigate(retorno || homeDe(u), { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo iniciar sesión.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] px-5">
      <div className="w-full max-w-md galarya-fade">
        <div className="flex justify-center mb-8">
          {/* La portada tiene fondo claro fijo: siempre el archivo para fondos claros. */}
          <LogoGalarya variante="claro" testId="login-logo"
            className="h-20 w-auto max-w-full sm:h-24" />
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-8">
          <h1 className="text-2xl font-semibold tracking-tight text-[#012472] mb-1">Consola de monitoreo</h1>
          <p className="text-sm text-slate-500 mb-7">Ingresa con tu correo y contraseña.</p>
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-600">Correo</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@correo.com"
                data-testid="login-email-input"
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-600">Contraseña</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password-input"
                className="rounded-xl"
              />
            </div>
            {error && (
              <p data-testid="login-error" className="text-sm text-red-600">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full rounded-xl bg-[#01CBE3] hover:bg-[#01B5CB] text-white h-11"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Iniciar sesión"}
            </Button>
          </form>
        </div>
        <p className="text-center text-xs text-slate-400 mt-6">
          La IA detecta. Humanos validan. Galarya cuida.
        </p>
      </div>
    </div>
  );
}
